import { useEffect, useMemo, useState } from "react";
import {
  Eye, Send, TrendingUp, Tag, Sparkles, Loader2,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabase";

// Mini-dashboard del buzón.
// Muestra vistas, envíos, tasa de conversión y top UTMs (origen/campaña).
// Lee leads con source='public_form' y agrega los source_metadata.

function aggregate(leads, key) {
  const counts = new Map();
  for (const l of leads) {
    const v = l.source_metadata?.[key];
    if (!v) continue;
    counts.set(v, (counts.get(v) || 0) + 1);
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
}

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="flex-1 bg-gray-50 rounded-lg p-3 text-center">
      <Icon size={16} className="mx-auto text-gray-400 mb-1" />
      <div className="text-lg font-semibold text-gray-900 leading-tight">
        {value}
      </div>
      <div className="text-xs text-gray-500">{label}</div>
      {hint && <div className="text-[11px] text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function TopList({ icon: Icon, title, items, emptyText }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="flex items-center gap-1.5 mb-2">
        <Icon size={13} className="text-gray-500" />
        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          {title}
        </h4>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-gray-400 italic">{emptyText}</p>
      ) : (
        <ul className="space-y-1">
          {items.map(([name, count]) => (
            <li
              key={name}
              className="flex items-center justify-between text-xs"
            >
              <span className="truncate text-gray-700 mr-2">{name}</span>
              <span className="font-semibold text-gray-900 flex-shrink-0">
                {count}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function LeadCaptureStats({ viewsCount, submissionsCount }) {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    supabase
      .from("leads")
      .select("source_metadata")
      .eq("student_id", user.id)
      .eq("source", "public_form")
      .then(({ data }) => {
        if (!alive) return;
        setLeads(data || []);
        setLoading(false);
      });
    return () => { alive = false; };
  }, [user.id]);

  const conversion = useMemo(() => {
    if (!viewsCount || viewsCount === 0) return null;
    return ((submissionsCount / viewsCount) * 100).toFixed(1);
  }, [viewsCount, submissionsCount]);

  const topSources = useMemo(() => aggregate(leads, "utm_source"), [leads]);
  const topCampaigns = useMemo(
    () => aggregate(leads, "utm_campaign"),
    [leads]
  );

  const hasAnyUtm = topSources.length > 0 || topCampaigns.length > 0;

  return (
    <div className="space-y-3">
      <div className="flex gap-3">
        <StatCard icon={Eye} label="Vistas" value={viewsCount ?? 0} />
        <StatCard icon={Send} label="Envíos" value={submissionsCount ?? 0} />
        <StatCard
          icon={TrendingUp}
          label="Conversión"
          value={conversion !== null ? `${conversion}%` : "—"}
          hint={conversion === null ? "Sin vistas aún" : undefined}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-3">
          <Loader2 size={14} className="animate-spin text-gray-400" />
        </div>
      ) : hasAnyUtm ? (
        <div className="grid grid-cols-2 gap-2">
          <TopList
            icon={Tag}
            title="Top orígenes"
            items={topSources}
            emptyText="Sin orígenes etiquetados"
          />
          <TopList
            icon={Sparkles}
            title="Top campañas"
            items={topCampaigns}
            emptyText="Sin campañas etiquetadas"
          />
        </div>
      ) : leads.length > 0 ? (
        <p className="text-xs text-gray-500 italic px-1">
          Aún no hay leads con etiquetas UTM. Comparte tus enlaces con UTMs
          para ver de dónde vienen.
        </p>
      ) : null}
    </div>
  );
}
